/**
 * Who operates the service — the seller identity and contact details the legal
 * pages (Terms / Privacy / Refunds) and the site footer print. Configured at
 * build time through VITE_ env vars so the legal text never needs a code change
 * when the entity or the support address changes.
 *
 * An unset value renders as a visible "[to be completed: …]" marker rather than
 * silently disappearing: a payment provider reviewing the site must never see a
 * document with a blank where the seller's name belongs, and the marker makes the
 * gap obvious to the owner before the site is submitted for review.
 */
export interface SiteInfo {
  /** Legal name of the business that operates Lorvok and appears as the seller. */
  entityName: string;
  /** Country whose law governs the Terms (the entity's country of registration). */
  entityCountry: string;
  /** Registered business address; optional (omitted from the pages when unset). */
  entityAddress: string | null;
  /**
   * True when the seller is a private person rather than a company (Paddle lets an
   * individual sell without incorporating): the documents then say "an individual
   * resident in <country>" instead of "registered in <country>".
   */
  entityIsIndividual: boolean;
  /** Public support / legal contact email. */
  supportEmail: string;
  /** Optional public support phone (a virtual number, never a personal one). */
  supportPhone: string | null;
  /** Optional EU representative (GDPR art. 27 / DSA art. 13): "name, address, email". */
  euRepresentative: string | null;
  /** Optional UK representative (UK GDPR art. 27): "name, address, email". */
  ukRepresentative: string | null;
  /** Canonical site origin, e.g. https://lorvok.app (no trailing slash). */
  siteUrl: string;
  /** True when every required value is configured (no placeholders shown). */
  complete: boolean;
}

/** Date the current versions of the legal documents take effect (ISO, UTC). */
export const LEGAL_EFFECTIVE_DATE = "2026-09-30";

/** Marker shown in place of an unconfigured value. */
export function missingValue(label: string): string {
  return `[to be completed: ${label}]`;
}

function read(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** Resolves the site info from an env record (import.meta.env in the app). */
export function resolveSiteInfo(env: Record<string, unknown>): SiteInfo {
  const entityName = read(env.VITE_LEGAL_ENTITY_NAME);
  const entityCountry = read(env.VITE_LEGAL_ENTITY_COUNTRY);
  const supportEmail = read(env.VITE_SUPPORT_EMAIL);
  const siteUrl = read(env.VITE_SITE_URL)?.replace(/\/+$/, "") ?? null;
  return {
    entityName: entityName ?? missingValue("legal entity name"),
    entityCountry: entityCountry ?? missingValue("country of registration"),
    entityAddress: read(env.VITE_LEGAL_ENTITY_ADDRESS),
    entityIsIndividual: read(env.VITE_LEGAL_ENTITY_TYPE)?.toLowerCase() === "individual",
    supportEmail: supportEmail ?? missingValue("support email"),
    supportPhone: read(env.VITE_SUPPORT_PHONE),
    euRepresentative: read(env.VITE_EU_REPRESENTATIVE),
    ukRepresentative: read(env.VITE_UK_REPRESENTATIVE),
    siteUrl: siteUrl ?? (typeof window !== "undefined" ? window.location.origin : ""),
    complete: Boolean(entityName && entityCountry && supportEmail && siteUrl)
  };
}

export const siteInfo: SiteInfo = resolveSiteInfo(import.meta.env);
